// Purchase State Service - OTO Completion Tracking & Purchase Progression
// Extracted from userDataPersistence.js for better separation of concerns

import { debugLog, debugError } from '../debugUtils';
import {
  getUserData,
  storeUserData,
  removeUserDataFields,
  clearPerformanceCache,
} from './userDataService';

/**
 * Check if a general state field has expired based on its timestamp
 * @param {number} timestamp - The timestamp when the field was set
 * @param {number} expirationHours - Hours after which the field expires
 * @returns {boolean} - true if expired, false if still valid
 */
const isStateFieldExpired = (timestamp, expirationHours) => {
  if (!timestamp) return true; // No timestamp = expired

  const now = Date.now();
  const expirationTime = expirationHours * 60 * 60 * 1000; // Convert hours to milliseconds
  const timeDiff = now - timestamp;

  const expired = timeDiff > expirationTime;

  if (expired) {
    debugLog(
      `🕐 State field expired - set ${Math.round(timeDiff / (60 * 1000))} minutes ago`
    );
  }

  return expired;
};

/**
 * Check if an OTO completion flag has expired
 * @param {number} timestamp - The timestamp when the flag was set
 * @param {number} expirationHours - Hours after which the flag expires (default: 2 hours)
 * @returns {boolean} - true if expired, false if still valid
 */
const isOTOFlagExpired = (timestamp, expirationHours = 2) => {
  return isStateFieldExpired(timestamp, expirationHours);
};

/**
 * Default purchase state for server-side rendering or no data
 */
const getDefaultPurchaseState = () => ({
  baseTicketQuantity: 2,
  baseTicketPrice: 10,
  selectedDate: '',
  email: '',
  customerId: '',
  hasCompletedReservedSeating: false,
  hasCompletedSpotlightPass: false,
  hasCompletedAdditionalTickets: false,
});

/**
 * Get the current purchase progression state with auto-expiring OTO flags
 * This tracks what OTO upgrades have been completed to calculate proper pricing
 * OTO flags automatically expire after 2 hours to handle abandoned flows
 */
export const getPurchaseState = () => {
  if (typeof window === 'undefined') return getDefaultPurchaseState();

  const userData = getUserData();
  if (!userData) return getDefaultPurchaseState();

  // ✅ DEBUG: Log the raw userData to track the timestamp issue
  if (
    userData.hasCompletedReservedSeating ||
    userData.hasCompletedSpotlightPass ||
    userData.hasCompletedAdditionalTickets
  ) {
    debugLog('🔍 OTO completion flags found in userData:', {
      reservedSeating: {
        hasFlag: !!userData.hasCompletedReservedSeating,
        hasTimestamp: !!userData.hasCompletedReservedSeatingTimestamp,
        timestamp: userData.hasCompletedReservedSeatingTimestamp,
        timestampAge: userData.hasCompletedReservedSeatingTimestamp
          ? Math.round(
              (Date.now() - userData.hasCompletedReservedSeatingTimestamp) /
                1000
            ) + 's'
          : 'no timestamp',
      },
      spotlightPass: {
        hasFlag: !!userData.hasCompletedSpotlightPass,
        hasTimestamp: !!userData.hasCompletedSpotlightPassTimestamp,
        timestamp: userData.hasCompletedSpotlightPassTimestamp,
        timestampAge: userData.hasCompletedSpotlightPassTimestamp
          ? Math.round(
              (Date.now() - userData.hasCompletedSpotlightPassTimestamp) / 1000
            ) + 's'
          : 'no timestamp',
      },
      additionalTickets: {
        hasFlag: !!userData.hasCompletedAdditionalTickets,
        hasTimestamp: !!userData.hasCompletedAdditionalTicketsTimestamp,
        timestamp: userData.hasCompletedAdditionalTicketsTimestamp,
        timestampAge: userData.hasCompletedAdditionalTicketsTimestamp
          ? Math.round(
              (Date.now() - userData.hasCompletedAdditionalTicketsTimestamp) /
                1000
            ) + 's'
          : 'no timestamp',
      },
      paymentIntentId: userData.paymentIntentId,
    });
  }

  // Check if OTO flags have expired (handles abandoned flows)
  const hasValidReservedSeating =
    userData.hasCompletedReservedSeating &&
    !isOTOFlagExpired(userData.hasCompletedReservedSeatingTimestamp);

  const hasValidSpotlightPass =
    userData.hasCompletedSpotlightPass &&
    !isOTOFlagExpired(userData.hasCompletedSpotlightPassTimestamp);

  const hasValidAdditionalTickets =
    userData.hasCompletedAdditionalTickets &&
    !isOTOFlagExpired(userData.hasCompletedAdditionalTicketsTimestamp);

  // ✅ DEBUG: Log the final validity check result
  if (
    userData.hasCompletedReservedSeating ||
    userData.hasCompletedSpotlightPass ||
    userData.hasCompletedAdditionalTickets
  ) {
    debugLog('🔍 OTO completion validity checks:', {
      reservedSeating: {
        rawFlag: !!userData.hasCompletedReservedSeating,
        hasTimestamp: !!userData.hasCompletedReservedSeatingTimestamp,
        isExpired: userData.hasCompletedReservedSeatingTimestamp
          ? isOTOFlagExpired(userData.hasCompletedReservedSeatingTimestamp)
          : 'no timestamp (counts as expired)',
        finalValid: hasValidReservedSeating,
      },
      spotlightPass: {
        rawFlag: !!userData.hasCompletedSpotlightPass,
        hasTimestamp: !!userData.hasCompletedSpotlightPassTimestamp,
        isExpired: userData.hasCompletedSpotlightPassTimestamp
          ? isOTOFlagExpired(userData.hasCompletedSpotlightPassTimestamp)
          : 'no timestamp (counts as expired)',
        finalValid: hasValidSpotlightPass,
      },
      additionalTickets: {
        rawFlag: !!userData.hasCompletedAdditionalTickets,
        hasTimestamp: !!userData.hasCompletedAdditionalTicketsTimestamp,
        isExpired: userData.hasCompletedAdditionalTicketsTimestamp
          ? isOTOFlagExpired(userData.hasCompletedAdditionalTicketsTimestamp)
          : 'no timestamp (counts as expired)',
        finalValid: hasValidAdditionalTickets,
      },
    });
  }

  return {
    // Base purchase info
    baseTicketQuantity: userData.quantity || 2,
    baseTicketPrice: 10, // Always $10 for base tickets
    selectedDate: userData.selectedDate || '',
    email: userData.email || '',
    customerId: userData.stripeCustomerId || '',

    // ✅ FIX: Include paymentIntentId for email confirmation system
    paymentIntentId: userData.paymentIntentId || '',

    // OTO completion tracking (with auto-expiration)
    hasCompletedReservedSeating: hasValidReservedSeating,
    hasCompletedSpotlightPass: hasValidSpotlightPass,
    hasCompletedAdditionalTickets: hasValidAdditionalTickets,

    // Additional ticket quantity (if applicable)
    additionalTicketsQuantity: userData.additionalTicketsQuantity || 0,

    // ✅ FIX: Include spotlightPassQuantity for email system
    spotlightPassQuantity: userData.spotlightPassQuantity || 0,

    // Pricing calculations will be done in hooks, not stored
  };
};

/**
 * Update purchase state when an OTO is completed
 */
export const updatePurchaseState = async updates => {
  if (typeof window === 'undefined') return;

  const currentData = getUserData() || {};
  const updatedData = { ...currentData, ...updates };
  await storeUserData(updatedData);
  clearPerformanceCache(); // Ensure fresh data on next read
};

/**
 * Clear sensitive purchase data (for security) but keep purchase summary for thank you page
 * This allows thank you page to show what they purchased without exposing payment data
 */
export const clearSensitivePurchaseData = async () => {
  if (typeof window === 'undefined') return;

  try {
    const sensitiveFields = [
      // Payment-related sensitive data
      'stripeCustomerId',
      'preloadedPaymentIntent',

      // Purchase flow tracking (flow is complete after thank you page)
      'initialPurchaseTimestamp',
    ];

    await removeUserDataFields(sensitiveFields);
    debugLog(
      '🔒 Cleared sensitive purchase data - keeping purchase summary for thank you page'
    );
  } catch (error) {
    debugError('Failed to clear sensitive purchase data:', error);
  }
};

/**
 * Clear all purchase state (used when purchase flow is completed)
 * This clears everything including email and customer ID for a fresh start
 */
export const clearAllPurchaseState = async () => {
  if (typeof window === 'undefined') return;

  try {
    const allPurchaseFields = [
      // Base purchase data
      'baseTicketQuantity',
      'quantity',
      'selectedDate',
      'email',
      'stripeCustomerId',

      // OTO completion flags
      'hasCompletedReservedSeating',
      'hasCompletedSpotlightPass',
      'hasCompletedAdditionalTickets',
      'additionalTicketsQuantity',
      'spotlightPassQuantity',

      // OTO completion timestamps
      'hasCompletedReservedSeatingTimestamp',
      'hasCompletedSpotlightPassTimestamp',
      'hasCompletedAdditionalTicketsTimestamp',

      // UI/UX state timestamps
      'exitIntentShownTimestamp',
      'reserveDiscountCodeTimestamp',

      // Purchase flow tracking
      'initialPurchaseTimestamp',

      // Payment data
      'preloadedPaymentIntent',
    ];

    await removeUserDataFields(allPurchaseFields);
    debugLog('🧹 Cleared all purchase state - fresh start');
  } catch (error) {
    debugError('Failed to clear all purchase state:', error);
  }
};

/**
 * Clear only OTO-specific purchase state (used for new booking flows)
 * Keeps email and base preferences for user convenience
 */
export const clearOTOPurchaseState = async () => {
  if (typeof window === 'undefined') return;

  try {
    const otoPurchaseFields = [
      // OTO completion flags - clear these for new booking
      'hasCompletedReservedSeating',
      'hasCompletedSpotlightPass',
      'hasCompletedAdditionalTickets',
      'additionalTicketsQuantity',
      'spotlightPassQuantity',

      // OTO completion timestamps
      'hasCompletedReservedSeatingTimestamp',
      'hasCompletedSpotlightPassTimestamp',
      'hasCompletedAdditionalTicketsTimestamp',

      // Customer payment data (they'll need to re-enter payment for new booking)
      'stripeCustomerId',

      // Purchase flow tracking
      'initialPurchaseTimestamp',

      // UI/UX state that should reset for fresh bookings
      'exitIntentShown',
      'exitIntentShownTimestamp',

      // Stale payment intents
      'preloadedPaymentIntent',
    ];

    await removeUserDataFields(otoPurchaseFields);
    debugLog('🧹 Cleared OTO purchase state - keeping user preferences');
  } catch (error) {
    debugError('Failed to clear OTO purchase state:', error);
  }
};

/**
 * Get purchase summary for thank you page customization
 * Returns safe, non-sensitive purchase data for display purposes
 */
export const getPurchaseSummary = () => {
  const state = getPurchaseState();
  if (!state) return null;

  return {
    baseTicketQuantity: state.baseTicketQuantity,
    selectedDate: state.selectedDate,
    hasCompletedReservedSeating: state.hasCompletedReservedSeating,
    hasCompletedSpotlightPass: state.hasCompletedSpotlightPass,
    hasCompletedAdditionalTickets: state.hasCompletedAdditionalTickets,
    additionalTicketsQuantity: state.additionalTicketsQuantity,

    // Calculate total tickets purchased
    totalTickets:
      (state.baseTicketQuantity || 0) + (state.additionalTicketsQuantity || 0),

    // Determine tier achieved
    finalTier: state.hasCompletedSpotlightPass
      ? 'Spotlight Pass'
      : state.hasCompletedReservedSeating
        ? 'Reserved Seating'
        : 'Entry Pass',
  };
};

/**
 * Mark initial purchase as completed (called after successful base purchase)
 * This helps track purchase completion for analytics and email workflows
 */
export const markInitialPurchaseCompleted = async () => {
  try {
    await updatePurchaseState({
      initialPurchaseTimestamp: Date.now(),
    });
    debugLog('🏁 Initial purchase marked as completed with timestamp');
  } catch (error) {
    debugError('Failed to mark initial purchase as completed:', error);
  }
};

/**
 * Get purchase state with auto-expiring UI/UX fields
 * This handles state that shouldn't persist indefinitely like exit intent, thank you modals, etc.
 */
export const getPurchaseStateWithExpiringFields = () => {
  const state = getPurchaseState();
  const userData = getUserData();

  if (!userData) return state;

  // Apply auto-expiration to UI/UX state fields only
  const result = { ...state };

  // Exit intent shown - expires after 24 hours (should be able to show again on return visits)
  if (userData.exitIntentShown && userData.exitIntentShownTimestamp) {
    result.exitIntentShown = !isStateFieldExpired(
      userData.exitIntentShownTimestamp,
      24
    );
  } else {
    result.exitIntentShown = userData.exitIntentShown || false;
  }

  // Discount code - expires after 2 hours (aligned with purchase-flow state)
  if (userData.reserveDiscountCode && userData.reserveDiscountCodeTimestamp) {
    const twoHours = 2; // Aligned with other purchase-flow state
    result.reserveDiscountCode = !isStateFieldExpired(
      userData.reserveDiscountCodeTimestamp,
      twoHours
    )
      ? userData.reserveDiscountCode
      : null;
  } else {
    result.reserveDiscountCode = userData.reserveDiscountCode || null;
  }

  return result;
};
